import { ProjectsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent, subscribeToServerEvent } from "@storytell/events";
import type { Project, ProjectId } from "@storytell/types";
import { createResource, createSignal, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";
import { mergePendingWrites, type PendingWrite } from "./pendingWrites";

export type ProjectsService = ReturnType<typeof createProjectsService>;

export const createProjectsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
}) => {
  const client = useStorytellClient();
  const [refreshTrigger, setRefreshTrigger] = createSignal(0);
  const [pendingWrites, setPendingWrites] = createSignal<Record<ProjectId, PendingWrite>>({});

  const noProjects: Record<ProjectId, Project> = {};
  const [projectsData, { refetch, mutate }] = createResource(
    () => deps.authReady() && deps.userId() && refreshTrigger() !== undefined,
    async (): Promise<Record<ProjectId, Project>> => {
      const request = ProjectsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch projects sync", response.error);
        // Keep the current store: a transient failure must not blank the
        // sidebar or drop a pending create that hasn't synced yet.
        return projectsData.latest;
      }
      const { merged, nextPending, changed } = mergePendingWrites(response.value.projects, pendingWrites(), Date.now());
      if (changed) {
        setPendingWrites(nextPending);
      }
      return merged;
    },
    {
      initialValue: noProjects,
      deferStream: true,
    }
  );

  const all = () => Object.values(projectsData.latest);
  const get = (id: ProjectId): Project | undefined => projectsData.latest?.[id];
  const set = (id: ProjectId, project: Project) => {
    setPendingWrites((prev) => ({ ...prev, [id]: { project, createdAt: Date.now() } }));
    mutate((prev) => ({ ...prev, [id]: project }));
  };
  // Drop the project locally and clear any pending write so a delete can't be
  // undone by the pending-write overlay re-merging it on the next sync.
  const remove = (id: ProjectId) => {
    mutate((prev) => {
      if (!prev?.[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setPendingWrites((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const canDelete = (id: ProjectId) => (get(id)?.grants?.["op.delete"] && !get(id)?.default) ?? false;
  const canUpdate = (id: ProjectId) => get(id)?.grants?.["op.update"] ?? false;
  const canCreate = (id: ProjectId) => get(id)?.grants?.["op.create"] ?? false;

  if (!isServer) {
    onMount(() => {
      const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
        setRefreshTrigger((prev) => prev + 1);
      });

      // Drop locally on delete so the sidebar reacts before refetch returns.
      const unsubscribeServerEvent = subscribeToServerEvent("cp.grants", (serverEvent) => {
        if (serverEvent.op === "op.delete") {
          const projectId = serverEvent.resource.projectId;
          if (projectId) {
            remove(projectId);
          }
        }

        void refetch();
      });

      onCleanup(() => {
        unsubscribeConnectivityRestored();
        unsubscribeServerEvent();
      });
    });
  }

  return {
    refetch,
    all,
    get,
    set,
    remove,
    canDelete,
    canUpdate,
    canCreate,
  };
};
